import {useMemo} from 'react';
import {LoadingBar} from '@/components/LoadingBar';
import {Tooltip} from '@/components/Tooltip';
import {Typography} from '@/components/Typography';
import {SalesCompare} from '@/map/@types';
import {useMapDataSource} from '@/map/api';
import {ColumnFlexBox, Flexbox, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {
  GrayCenterBox, HighlightBox, SectionBlock, SectionSubTitle, SectionTitle
} from '../../StyledComponents';
import {SalesDayTimeSynchronizedAreaChart} from '../Chart/SalesDayTimeSynchronizedAreaChart';
import {EMPTY_ITEM_TYPES, SALES_TIME_LABELS} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {SalesDataProps} from '../interface';

export const SalesTimeCompare = ({
  isPending,
  error,
  data: rawdata,
}: SalesDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const houseHoldsMetadata = useMemo(
    () =>
      dataSource?.find(
        (source) =>
          source.key === 'kodata' && source.data.indexOf('매출 정보') !== -1
      ),
    [dataSource]
  );

  const data = useMemo(() => {
    return rawdata?.map((i) => i.time);
  }, [rawdata]);

  return (
    <SectionBlock>
      <Flexbox>
        <SectionTitle>결제 비중(시간)</SectionTitle>
        <Tooltip
          openOnClick
          closeEvents={{
            mouseout: false,
            blur: false,
            click: true,
          }}
          iconStyle={{marginLeft: 2}}
          content={`새벽 : 1:00- 5:00, 오전 6:00-10:00, 점심 11:00-14:00, 오후 15:00-17:00 , 저녁 18:00-20:00, 밤 21:00-24:00`}
        />
      </Flexbox>
      <SectionSubTitle>{`${houseHoldsMetadata?.source || '카드사'}∙${
        houseHoldsMetadata?.baseDate || ''
      }`}</SectionSubTitle>
      <PaddingBox top={16}>
        {isPending ? (
          <GrayCenterBox>
            <LoadingBar color={palette.textSecondary} />
          </GrayCenterBox>
        ) : error ? (
          <Typography variant="medium12" color={palette.textSecondary}>
            분석 실패
          </Typography>
        ) : !data ? (
          <EmptyItem type={EMPTY_ITEM_TYPES.Sales} />
        ) : (
          <SalesTimeChartBox items={data} />
        )}
      </PaddingBox>
    </SectionBlock>
  );
};

const SalesTimeChartBox = ({items}: {items: Array<SalesCompare['time']>}) => {
  const compareList = items.map((_, idx) => `비교 상권 ${idx + 1}`);

  const maxData = items.map((item) => {
    const sortedData = item
      .map((value, index) => ({value: Math.round(value * 10) / 10 || 0, index}))
      .sort((a, b) => b.value - a.value);
    const maxValues = sortedData.filter(
      (data, idx, arr) => idx < 3 && data.value === arr[0].value
    );
    return {
      maxValues: maxValues.map((data) => ({
        value: data.value,
        index: data.index,
      })),
    };
  });

  const makeMaxPerIndex = (index: number) => {
    return maxData[index].maxValues;
  };

  return (
    <ColumnFlexBox gap={16}>
      {items.map((item, index) => {
        return (
          <SalesDayTimeSynchronizedAreaChart
            key={`결제비중시간-${index}`}
            type={'결제비중시간'}
            data={item}
            compareItemName={compareList[index]}
            compareItemIndex={index}
            labels={SALES_TIME_LABELS}
          />
        );
      })}
      <HighlightBox>
        {!!compareList.length && (
          <>
            {compareList.map((item, index) => {
              const maxPerIndex = makeMaxPerIndex(index);

              return (
                <Typography
                  variant="medium12"
                  as="div"
                  key={`SalesTime-CompareList-${index}`}
                >
                  <Typography color={palette.primary500} fontWeight="700">
                    {item}
                  </Typography>
                  은/는{' '}
                  {!!maxPerIndex?.length ? (
                    <>
                      <strong>{`${maxData[index].maxValues
                        .map((data) => `${SALES_TIME_LABELS[data.index]}`)
                        .join(', ')}`}</strong>
                      의 결제 비중이 가장 높아요.
                    </>
                  ) : (
                    '정보가 없어요.'
                  )}
                </Typography>
              );
            })}
          </>
        )}
      </HighlightBox>
    </ColumnFlexBox>
  );
};
